import {
  AlertTriangle,
  BarChart3,
  Bell,
  BookCheck,
  Gavel,
  BriefcaseBusiness,
  Building2,
  Calendar,
  CalendarDays,
  CircleUserRound,
  ClipboardCheck,
  CreditCard,
  Clock,
  Contact,
  DollarSign,
  FileBarChart2,
  FileSpreadsheet,
  FileSignature,
  FolderOpen,
  Gift,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  Megaphone,
  MessageSquare,
  Receipt,
  Scale,
  ScrollText,
  Settings,
  Shield,
  ShieldCheck,
  TimerReset,
  TreePalm,
  Tv,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react'

import { getPermissionForPath } from '@/config/routePermissions'
import type { UsePermissionsResult } from '@/hooks/usePermissions'
import { usePermissions } from '@/hooks/usePermissions'
import { useMe } from '@/features/settings/hooks/useMe'
import { useAuthStore } from '@/stores/useAuthStore'

export type RoleScope = 'all' | 'company' | 'self'

export type NavItemLink = {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  adminOnly?: boolean
  /** Hidden unless this feature resolves on for the organization. */
  feature?: string
}

export type NavSection = {
  label: string
  items: NavItemLink[]
  /** Hidden for users whose org-level scope is 'all' (admin/auditor/accountant) — they aren't employees of a specific company. */
  hideForOrgWideScope?: boolean
  /** Icon for the Sidebar menu row (and its single icon when the sidebar is collapsed). */
  icon?: React.ComponentType<{ className?: string }>
}

/**
 * Single source of truth for primary navigation, shared by the Sidebar and the
 * command palette so the two never drift. Dashboard and Companies live in their
 * own "Overview" section here — the Sidebar still renders them via
 * SidebarDashboardAndCompanies, but the palette reads them from this model.
 */
export function buildNavSections(): NavSection[] {
  return [
    {
      label: 'Overview',
      items: [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/companies', label: 'Companies', icon: Building2 },
        { to: '/companies/setup-wizard', label: 'Setup Wizard', icon: FileSpreadsheet },
      ],
    },
    {
      label: 'Workforce',
      icon: Users,
      items: [
        { to: '/members', label: 'Members', icon: Users },
        { to: '/users', label: 'Users', icon: UserRound },
        { to: '/candidates', label: 'Candidates', icon: BriefcaseBusiness },
        { to: '/documents', label: 'Documents', icon: FolderOpen },
        { to: '/hr', label: 'People & HR', icon: Contact },
      ],
    },
    {
      label: 'Time & Attendance',
      icon: Clock,
      items: [
        { to: '/attendance', label: 'Attendance', icon: Clock },
        { to: '/shifts', label: 'Shifts', icon: Calendar },
        { to: '/leaves', label: 'Leave', icon: TreePalm },
        { to: '/time-corrections', label: 'Time corrections', icon: TimerReset },
        { to: '/wallboard', label: 'WallBoard', icon: Tv },
      ],
    },
    {
      label: 'Payroll',
      icon: DollarSign,
      items: [
        { to: '/payroll', label: 'Payroll', icon: DollarSign },
        { to: '/bonuses', label: 'Bonuses', icon: Gift },
        { to: '/penalties', label: 'Penalties', icon: AlertTriangle },
        { to: '/security-ledger', label: 'Security deposit', icon: Shield },
        { to: '/tax-rules', label: 'Tax Rules', icon: Scale },
      ],
    },
    {
      label: 'Expenses',
      icon: Receipt,
      items: [
        { to: '/expenses', label: 'Expenses', icon: Receipt },
        { to: '/expense-analytics', label: 'Expense Analytics', icon: BarChart3 },
        { to: '/expense-approvals', label: 'Expense Approvals', icon: ClipboardCheck },
      ],
    },
    {
      label: 'Communication',
      icon: MessageSquare,
      items: [
        { to: '/channels', label: 'Channels', icon: MessageSquare, feature: 'channels.messaging' },
        { to: '/announcements', label: 'Announcements', icon: Megaphone },
        { to: '/boards', label: 'Boards', icon: LayoutGrid },
      ],
    },
    {
      label: 'Reports',
      icon: FileBarChart2,
      items: [
        { to: '/reports', label: 'Reports', icon: FileBarChart2 },
        { to: '/audit-logs', label: 'Audit logs', icon: ScrollText },
      ],
    },
    {
      label: 'My Workspace',
      icon: CircleUserRound,
      hideForOrgWideScope: true,
      items: [
        { to: '/my-shifts', label: 'My Shifts', icon: CalendarDays },
        { to: '/my-attendance', label: 'My Attendance', icon: Clock },
        { to: '/my-time-corrections', label: 'My Time Corrections', icon: TimerReset },
        { to: '/my-leaves', label: 'My Leaves', icon: TreePalm },
        { to: '/my-bonuses', label: 'My Bonuses', icon: Gift },
        { to: '/my-expenses', label: 'My Expenses', icon: Receipt },
        { to: '/my-deposits', label: 'My Security Deposit', icon: Shield },
        { to: '/my-documents', label: 'My Documents', icon: FolderOpen },
        { to: '/my-letters', label: 'My Letters', icon: FileSignature },
        { to: '/my-policies', label: 'My Policies', icon: BookCheck },
        { to: '/my-hr-cases', label: 'My HR Cases', icon: Gavel },
        { to: '/payroll/me', label: 'My Payroll', icon: Wallet },
      ],
    },
    {
      label: 'Notifications',
      icon: Bell,
      items: [{ to: '/notifications', label: 'Notifications', icon: Bell }],
    },
    {
      label: 'Settings',
      icon: Settings,
      items: [
        { to: '/organization', label: 'Organization', icon: Building2 },
        // Gated with the route (settings: update): only people who can change the plan see it.
        { to: '/billing', label: 'Billing & plan', icon: CreditCard },
        { to: '/roles', label: 'Roles', icon: Shield },
        {
          to: '/settings/permissions-matrix',
          label: 'Permissions Matrix',
          icon: ShieldCheck,
          adminOnly: true,
        },
        // No route permission for /support: every signed-in user can raise a ticket.
        { to: '/support', label: 'Help & support', icon: LifeBuoy },
        { to: '/settings', label: 'General settings', icon: Settings },
      ],
    },
  ]
}

export function normalizeRoleScope(scope?: string): RoleScope | undefined {
  if (scope === 'team') return 'company'
  if (scope === 'all' || scope === 'company' || scope === 'self') return scope
  return undefined
}

export interface NavVisibilityContext {
  perms: Pick<UsePermissionsResult, 'can' | 'canAny' | 'hasModule' | 'isLoading'>
  isAdminRole: boolean
  effectiveScope?: RoleScope
  /** Resolved feature flags; absent while still loading. */
  features?: Record<string, boolean>
}

/**
 * True when a nav item is visible to the current user. Mirrors the Sidebar's
 * filter exactly (adminOnly → getPermissionForPath → canAny/can/hasModule),
 * with `/settings` always allowed.
 */
export function isNavItemVisible(item: NavItemLink, ctx: NavVisibilityContext): boolean {
  const { perms, isAdminRole, features } = ctx
  if (item.adminOnly && !isAdminRole) return false
  // Hiding the link is courtesy, not control — the API is what enforces this.
  // Unknown or unresolved means hidden: a feature nobody has published should
  // not appear while the answer is still in flight.
  if (item.feature && features?.[item.feature] !== true) return false
  const permission = getPermissionForPath(item.to)
  if (!permission || item.to === '/settings') return true
  if (perms.isLoading) return false
  if (permission.actions && permission.actions.length > 0) {
    return perms.canAny(permission.module, permission.actions)
  }
  if (permission.action !== undefined) {
    return perms.can(permission.module, permission.action)
  }
  return perms.hasModule(permission.module)
}

/**
 * Returns sections filtered to what the current user can access, applying the
 * `hideForOrgWideScope` rule for admins / org-wide-scope roles. Empty sections
 * are dropped. Both the Sidebar and the command palette consume this.
 */
export function getVisibleNavSections(ctx: NavVisibilityContext): NavSection[] {
  return buildNavSections()
    .filter(
      (section) =>
        !(section.hideForOrgWideScope && (ctx.effectiveScope === 'all' || ctx.isAdminRole))
    )
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => isNavItemVisible(item, ctx)),
    }))
    .filter((section) => section.items.length > 0)
}

/**
 * Hook that resolves the current user's permission/scope context and returns the
 * visible nav sections. Use in components that already run inside the auth shell.
 */
export function useVisibleNavSections(): NavSection[] {
  const { can, canAny, hasModule, isLoading } = usePermissions()
  const userScope = useAuthStore((s) => s.user?.scope)
  const roleSlug = useAuthStore((s) => s.user?.roleSlug)
  const { data: me } = useMe()
  const meRoleSlug = typeof me?.roleId === 'string' ? undefined : me?.roleId.slug
  const meRoleScope =
    typeof me?.roleId === 'string' ? undefined : normalizeRoleScope(me?.roleId.scope)
  const effectiveRoleSlug = (meRoleSlug ?? roleSlug)?.toLowerCase()
  const effectiveScope = meRoleScope ?? userScope
  const isAdminRole = effectiveRoleSlug === 'admin'

  return getVisibleNavSections({
    perms: { can, canAny, hasModule, isLoading },
    isAdminRole,
    effectiveScope,
  })
}
